<si-form-item [label]="label() | translate" [required]="required()" [formErrorMapper]="errorMapper()">
    <div class="form-control-wrapper relative w-full" [class.is-invalid]="isInvalid()">
        <input
            type="text"
            class="sr-only !absolute pointer-events-none"
            [formControl]="control"
            tabindex="-1"
            aria-hidden="true"
        />
        <button
            #triggerButton
            type="button"
            class="select dropdown-toggle flex items-center justify-between gap-2 w-full text-left border [--border-color:var(--element-ui-2)] !border-[color:var(--border-color)] rounded-[var(--element-input-radius)] bg-[var(--element-base-1)] bg-clip-padding py-[7px] pl-[7px] pr-[12px] text-sm font-normal leading-[1.1428571429] text-[color:var(--element-text-primary)] appearance-none"
            aria-haspopup="listbox"
            [class.show]="open()"
            [attr.aria-expanded]="open()"
            [class.!border-danger]="isInvalid()"
            [attr.aria-invalid]="isInvalid()"
            [disabled]="control.disabled || readonly()"
            (click)="openPanel()"
        >
            <div class="flex flex-[1_1_0] min-w-0 items-center overflow-hidden gap-1">
                @if (selectedDisplayItems().length === 0) {
                    <span class="grow text-[color:var(--element-text-secondary)]">
                        {{ placeholder() | translate }}
                    </span>
                } @else {
                    <div #namesContainer class="flex flex-[1_1_0] min-w-0 items-center overflow-hidden gap-1">
                        @for (
                            item of selectedDisplayItems();
                            track item.translationKey + ':' + item.isCustom;
                            let i = $index
                        ) {
                            <span data-name-item class="text-nowrap">
                                {{ item.isCustom ? item.translationKey : (item.translationKey | translate)
                                }}{{ i < selectedDisplayItems().length - 1 - overflowCount() ? ',' : '' }}
                            </span>
                        }
                        <span
                            #overflowBadge
                            class="invisible text-nowrap shrink-0 rounded-[var(--element-radius-3)] bg-[var(--element-base-0)] px-2 ml-1"
                        >
                            +{{ overflowCount() }}
                        </span>
                    </div>
                }
            </div>
            <i
                aria-hidden="true"
                class="dropdown-caret icon element-down-2 flex-shrink-0 text-[color:var(--element-text-primary)]"
            ></i>
        </button>

        @if (open()) {
            <div
                class="absolute left-0 right-0 top-full z-10 mt-1 w-full dropdown-menu show max-h-[360px] overflow-y-auto p-0"
                data-subject-dropdown
                role="listbox"
                cdkTrapFocus
                tranCustomValueSelect
                (keydown.escape)="closePanel()"
            >
                @let dropdownRowClasses =
                    'dropdown-item focus-inside flex items-center gap-3 w-full hover:bg-[var(--element-base-1-hover)] focus-within:bg-[var(--element-base-1-selected)]';
                @let optionButtonClasses =
                    'flex w-full min-w-0 items-center gap-3 grow bg-transparent border-none text-left !py-[6px] hover:text-[var(--element-text-primary)] focus:text-[var(--element-text-primary)] focus:bg-[var(--element-base-1-selected)] focus:outline-none focus-within:text-[var(--element-text-primary)] focus-within:bg-[var(--element-base-1-selected)] focus-within:outline-none';
                @let optionCheckboxClasses = 'form-check-input shrink-0 pointer-events-none';

                @for (item of predefinedOptions(); track item.id) {
                    <div [class]="dropdownRowClasses">
                        <button
                            type="button"
                            [class]="optionButtonClasses"
                            [class.opacity-50]="item.disabled"
                            [class.cursor-not-allowed]="item.disabled"
                            role="option"
                            tabindex="-1"
                            data-option-btn
                            [attr.aria-selected]="item.selected"
                            [disabled]="item.disabled"
                            (click)="togglePredefined(item.option)"
                        >
                            @if (!singleSelection()) {
                                <input
                                    [class]="optionCheckboxClasses"
                                    type="checkbox"
                                    tabindex="-1"
                                    [checked]="item.selected"
                                    [attr.aria-hidden]="true"
                                />
                            }
                            <span>
                                {{
                                    (displayTranslationPrefix() ? displayTranslationPrefix() + item.label : item.label)
                                        | translate
                                }}
                            </span>
                        </button>
                    </div>
                }

                @for (item of allCustomItems(); track item.value) {
                    @let customItemDisabled = isCustomItemDisabled(item.checked);
                    <div [class]="dropdownRowClasses">
                        <button
                            type="button"
                            [class]="optionButtonClasses"
                            [class.opacity-50]="customItemDisabled"
                            [class.cursor-not-allowed]="customItemDisabled"
                            tabindex="-1"
                            data-option-btn
                            [disabled]="customItemDisabled"
                            (click)="toggleCustomItem(item.value, item.checked)"
                        >
                            @if (!singleSelection()) {
                                <input
                                    [class]="optionCheckboxClasses"
                                    type="checkbox"
                                    tabindex="-1"
                                    [checked]="item.checked"
                                    [attr.aria-hidden]="true"
                                />
                            }
                            <span>{{ item.value }}</span>
                        </button>
                        <button
                            type="button"
                            class="btn btn-circle btn-ghost ml-auto focus-visible:[outline:var(--element-button-focus-width)_solid_var(--element-focus-default)] focus-visible:[outline-offset:var(--element-button-focus-overlay-width)]"
                            tabindex="-1"
                            data-delete-btn
                            (click)="$event.stopPropagation(); removeCustomItem(item.value, item.checked)"
                            [attr.aria-label]="'components.custom-value-multi-select.remove-custom-value' | translate"
                            [title]="'components.custom-value-multi-select.remove-custom-value' | translate"
                            [disabled]="control.disabled || readonly()"
                        >
                            <i class="element-delete icon"></i>
                        </button>
                    </div>
                }

                <div data-custom-footer class="sticky bottom-0 z-[1] bg-[var(--bs-dropdown-bg,var(--element-base-1))]">
                    <div class="dropdown-divider m-0"></div>
                    <div class="px-3 py-2">
                        <div class="flex gap-2">
                            <input
                                #addInput
                                type="text"
                                class="form-control form-control-sm flex-1"
                                [placeholder]="'components.custom-value-multi-select.add-custom-value' | translate"
                                [disabled]="isAddCustomDisabled()"
                                (keydown.enter)="addCustom(addInput.value); addInput.value = ''"
                            />
                            <button
                                type="button"
                                class="btn btn-circle btn-tertiary"
                                [disabled]="isAddCustomDisabled()"
                                (click)="addCustom(addInput.value); addInput.value = ''"
                            >
                                <i class="element-plus icon"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        }
    </div>
</si-form-item>
